/**
 * DEV-026 · Shared "New to-do" modal.
 *
 * The modal creates an ad-hoc task via the existing `POST /tasks`
 * endpoint — the SAME collection that powers the Tasks workspace.
 * A to-do IS an ad-hoc task; the workspace label differs but the
 * underlying record is one shared row (no duplicate collection).
 *
 * `project_id` is optional so a standalone personal to-do can be
 * created without a customer or project. When `projectOptions` is
 * supplied, callers can associate the to-do with an authorised
 * project; the selector never exposes the full project directory
 * — it only presents projects the caller is already authorised for.
 */
import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, ListPlus, X } from "lucide-react";

import { api, formatApiError } from "@/lib/api";

const PRIORITIES = ["Low", "Normal", "High", "Critical"];

export default function NewTodoModal({
    open, onCancel, onCreated,
    projectOptions = [],  // [{ id, label }] — optional authorised projects
    defaultProjectId = "",
}) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [priority, setPriority] = useState("Normal");
    const [dueDate, setDueDate] = useState("");
    const [projectId, setProjectId] = useState(defaultProjectId);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setTitle(""); setDescription(""); setPriority("Normal");
        setDueDate(""); setProjectId(defaultProjectId);
        setBusy(false); setError(null);
        const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const canSubmit = title.trim().length > 0 && !busy;

    async function submit(e) {
        e?.preventDefault?.();
        if (!canSubmit) return;
        setBusy(true);
        setError(null);
        try {
            const body = {
                title: title.trim(),
                description: description.trim() || undefined,
                priority,
                task_category: "General",
                due_date: dueDate || undefined,
            };
            if (projectId) body.project_id = projectId;
            const created = await api.createTask(body);
            onCreated?.(created);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = (typeof detail === "object" ? detail?.code : null) || null;
            const msg = (typeof detail === "object" ? detail?.message || detail?.detail : null)
                      || formatApiError(detail)
                      || "Failed to create to-do";
            const humanised = code === "TASK_CREATE_NOT_AUTHORISED"
                ? "You are not authorised to create tasks on this project."
              : e?.response?.status === 403
                ? "You do not have permission to create tasks."
              : msg;
            setError({ code, message: humanised });
        } finally {
            setBusy(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0B1220]/50 backdrop-blur-sm px-4"
            role="dialog"
            aria-modal="true"
            data-testid="new-todo-modal"
        >
            <form
                onSubmit={submit}
                className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden"
            >
                <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#131B2F]">
                        <ListPlus size={16} className="text-[#131B2F]" />
                        New to-do
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        aria-label="Close"
                        className="w-8 h-8 rounded-md text-[#4B5565] hover:text-[#131B2F] hover:bg-[#F5F5F7] flex items-center justify-center"
                    >
                        <X size={14} />
                    </button>
                </div>

                <div className="px-6 py-4 space-y-3 text-sm text-[#131B2F]">
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. Call Sarah re quote"
                            data-testid="new-todo-title"
                            autoFocus
                            maxLength={200}
                            className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#131B2F]"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">Priority</label>
                            <select
                                value={priority}
                                onChange={(e) => setPriority(e.target.value)}
                                data-testid="new-todo-priority"
                                className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-2 outline-none focus:border-[#131B2F]"
                            >
                                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">Due date</label>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                data-testid="new-todo-due"
                                className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-3 outline-none focus:border-[#131B2F]"
                            />
                        </div>
                    </div>

                    {/* DEV-026 · Always render the optional project
                        selector so users see the personal/no-project
                        option even when no authorised projects are
                        available. Personal is the safe default;
                        picking a project is optional. */}
                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">
                            Project (optional)
                        </label>
                        <select
                            value={projectId}
                            onChange={(e) => setProjectId(e.target.value)}
                            data-testid="new-todo-project"
                            className="w-full h-10 rounded-md border border-[#E5E7EB] bg-white px-2 outline-none focus:border-[#131B2F]"
                        >
                            <option value="">— Personal (no project) —</option>
                            {projectOptions.map((p) => (
                                <option key={p.id} value={p.id}>{p.label}</option>
                            ))}
                        </select>
                        <div className="mt-1 text-[11px] text-[#4B5565]">
                            {projectOptions.length === 0
                                ? "No authorised projects to link. This to-do will be created as a personal item."
                                : "Leave blank for a standalone personal to-do. Project choices are limited to projects you are already authorised for."}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[11px] uppercase tracking-[0.1em] text-[#4B5565] mb-1">Notes (optional)</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            data-testid="new-todo-notes"
                            className="w-full rounded-md border border-[#E5E7EB] bg-white p-2 outline-none focus:border-[#131B2F]"
                        />
                    </div>

                    {error && (
                        <div
                            className="rounded-md border border-[#DA3535]/40 bg-[#FEF2F2] px-3 py-2 text-xs text-[#7C1D1D]"
                            data-testid="new-todo-error"
                            data-error-code={error.code || undefined}
                        >
                            {error.message}
                        </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t border-[#E5E7EB] bg-[#FAFAFC] flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        data-testid="new-todo-cancel"
                        className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!canSubmit}
                        data-testid="new-todo-submit"
                        className="h-10 px-4 rounded-md bg-[#131B2F] hover:bg-[#0F1526] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {busy && <Loader2 size={14} className="animate-spin" />}
                        Create to-do
                    </button>
                </div>
            </form>
        </div>
    );
}
